// components/orders/DateRangeSelector.jsx
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "@/components/icons_new";

const RANGE_OPTIONS = [
  { label: "Today", value: "today", description: "Compared to yesterday up to current hour" },
  { label: "Last 7 days", value: "7days", description: "Compared to the previous 7 days" },
  { label: "Last 30 days", value: "30days", description: "Compared to the previous 30 days" },
];

export default function DateRangeSelector({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [portalContainer, setPortalContainer] = useState(null);
  const [menuStyles, setMenuStyles] = useState({ top: 0, left: 0, minWidth: 0 });

  function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  // Create a portal container attached to the body so the dropdown can escape overflow contexts
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const container = document.createElement("div");
    document.body.appendChild(container);
    setPortalContainer(container);
    return () => {
      document.body.removeChild(container);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return undefined;
    function handleClickOutside(e) {
      if (
        triggerRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Reposition dropdown while open to keep it aligned with trigger
  useEffect(() => {
    if (!isOpen) return;

    function updatePosition() {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyles({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        minWidth: rect.width,
      });
    }
    const debouncedUpdate = debounce(updatePosition, 100);

    debouncedUpdate();
    window.addEventListener("resize", debouncedUpdate);
    window.addEventListener("scroll", debouncedUpdate, true);
    return () => {
      window.removeEventListener("resize", debouncedUpdate);
      window.removeEventListener("scroll", debouncedUpdate, true);
    };
  }, [isOpen]);

  const selected = RANGE_OPTIONS.find((opt) => opt.value === value);

  return (
    <div className="inline-block border ml-[1em] border-[#dddddd] rounded-md">
      {/* Date range toggle */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2 px-3 py-2 rounded-md transition 
          ${isOpen ? "bg-[#ececec]" : "hover:bg-[#ececec]"}`}
      >
        <Calendar className="h-4 w-4 text-[#333333]" />
        <span className="text-sm font-medium text-[#333333]">
          {selected?.label || "Select Range"}
        </span>
      </button>
      
      {/* Date range modal */}
      {isOpen && portalContainer &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            tabIndex={-1}
            style={{
              position: "absolute",
              top: `${menuStyles.top}px`,
              left: `${menuStyles.left}px`,
              minWidth: `${menuStyles.minWidth}px`,
              zIndex: 1050,
            }}
            className="rounded-lg border bg-white shadow-md"
          >
            <div className="w-64">
              {RANGE_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`px-4 py-3 cursor-pointer hover:bg-[#f8f8f8] transition ${
                    value === opt.value ? "bg-[#e1e3f2]" : ""
                  }`}
                >
                  <p className="text-sm font-medium text-[#333333]">{opt.label}</p>
                  <p className="text-xs text-[#868686]">{opt.description}</p>
                </div>
              ))}
            </div>
          </div>,
          portalContainer
        )
      }
    </div>
  );
}